import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_DYSLEXIA_SCREENING_CHILDREN,
  PERSONALITY_TEST_ASSESSMENT_ID,
  BIG_FIVE_TEST_ASSESSMENT_ID,
  traitTitleKeyMap,
} from "../../utils/general-config";

import { getDyslexiaChildrenLevelFromScore } from "../../utils/assessment-utils";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  getCookie,
  getDyslexiaPageImage,
  getEnneagramBannerImage,
  snakeToTitleCase,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import ExpandableSection from "../../components/expandable-collapsible/component";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";
import InviteFriend from "../../components/invite-friend";
import { CallToActionSection } from "../../components/call-to-action/component";
import ReportWrapper from "../../components/report-wrapper";

const DyslexiaScreeningChildrenReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,

  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getOrderDataByUserIdConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const bannerImage = getEnneagramBannerImage();
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }
        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_DYSLEXIA_SCREENING_CHILDREN[currentLocale] ||
            MODEL_TYPE_DYSLEXIA_SCREENING_CHILDREN[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const result1ImageText = "dyslexia-report-1";
  const result1Image = getDyslexiaPageImage(result1ImageText);

  const observationsContent = contentData?.observations;
  const meaningContent = contentData?.meaning;
  const tipsContent = contentData?.tips;
  const disclaimer = contentData?.disclaimer;
  const nextStepsContent = contentData?.nextSteps;
  const overviewcontnt = contentData?.overview;

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;
    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={isString ? title : "Banner"}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        <div className="relative h-full flex items-start" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start pr-10 md:pr-16">
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl font-bold text-white text-left break-words whitespace-normal"
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span className="block sm:inline">{firstWord}</span>
                    {restOfTitle && (
                      <>
                        <span className="hidden sm:inline"> </span>
                        <span className="block sm:inline">{restOfTitle}</span>
                      </>
                    )}
                  </span>
                ) : (
                  title
                )}
              </h2>

              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton
              onClick={onClick1}
              id="dyslexia-children-report-take-personality-test-button"
            />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.strengthMesssge")}
            </p>
            <TakeTestButton
              onClick={onClick2}
              id="dyslexia-children-report-take-big-five-test-button"
            />
          </div>
        </div>
      </section>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        <defs>
          <filter
            id={`shadow-${character || "bullet"}-${actualSize}`}
            x="-30%"
            y="-30%"
            width="160%"
            height="160%"
          >
            <feGaussianBlur
              stdDeviation="3"
              in="SourceAlpha"
              result="blurOut"
            />

            <feOffset dx="0" dy="2" in="blurOut" result="offsetBlur" />

            <feComponentTransfer in="offsetBlur" result="coloredShadow">
              <feFuncA type="linear" slope="0.3" />
            </feComponentTransfer>

            <feMerge>
              <feMergeNode in="coloredShadow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          filter={`url(#shadow-${character || "bullet"}-${actualSize})`}
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const MeaningCard = ({ meaning }) => {
    if (!meaning || !Array.isArray(meaning?.Content)) {
      return null;
    }

    return (
      <div className="p-4 md:p-6 rounded-2xl shadow-lg bg-purple-100 flex flex-col h-full">
        <div className="flex flex-row gap-2 items-start">
          <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
            <CircleIcon character="i" size={22} />
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            {meaning?.RootHeading}
          </h2>
        </div>

        <p className="text-gray-700 mt-4 pl-10">{meaning?.FlavorText}</p>

        <div className="space-y-4 mt-4 pl-10">
          {meaning?.Content?.map((section, index) => {
            if (!section || typeof section !== "object") return null;

            return (
              <div key={index} className="mb-3">
                <h3 className="font-bold text-gray-700 mb-2">
                  {section?.Heading || `Step ${index + 1}`}
                </h3>
                <div className="mt-1">
                  <div className="flex items-start gap-2 pl-1">
                    <div className="shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[2px]">
                      <CircleIcon character="" size={10} />
                    </div>
                    <p className="text-gray-700">{section.Content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const TipsCard = ({ tips }) => {
    if (!tips || !Array.isArray(tips?.Content)) {
      return null;
    }

    return (
      <div className="p-4 md:p-6 rounded-2xl shadow-lg bg-yellow-100 flex flex-col h-full">
        <div className="flex flex-row gap-2 items-start">
          <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
            <CircleIcon character="💡" size={22} />
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            {tips?.RootHeading}
          </h2>
        </div>

        <p className="text-gray-700 mt-4 pl-10">{tips?.FlavorText}</p>

        <div className="space-y-4 mt-4 pl-10">
          {tips?.Content?.map((section, index) => {
            if (!section || typeof section !== "object") return null;

            return (
              <div key={index} className="mb-3">
                <h3 className="font-bold text-gray-700 mb-2">
                  {section?.Heading || `Tip ${index + 1}`}
                </h3>
                <div className="mt-1">
                  <div className="flex items-start gap-2 pl-1">
                    <div className="shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[2px]">
                      <CircleIcon character="" size={10} />
                    </div>
                    <p className="text-gray-700">{section.Content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const NextStepsCard = ({ nextSteps }) => {
    if (!nextSteps || !Array.isArray(nextSteps?.Content)) {
      return null;
    }

    return (
      <div className="p-4 md:p-6 rounded-2xl shadow-lg bg-green-100 flex flex-col h-full">
        <div className="flex flex-row gap-2 items-start">
          <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
            <CircleIcon character="➡️" size={22} />
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            {nextSteps?.RootHeading}
          </h2>
        </div>

        <p className="text-gray-700 mt-4 pl-10">{nextSteps?.FlavorText}</p>

        <div className="space-y-4 mt-4 pl-10">
          {nextSteps?.Content?.map((section, index) => {
            if (!section || typeof section !== "object") return null;

            return (
              <div key={index} className="mb-3">
                <h3 className="font-bold text-gray-700 mb-2">
                  {section?.Heading || `Step ${index + 1}`}
                </h3>
                <div className="mt-1">
                  <div className="flex items-start gap-2 pl-1">
                    <div className="shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[2px]">
                      <CircleIcon character="" size={10} />
                    </div>
                    <p className="text-gray-700">{section.Content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const OverviewCard = ({ overview }) => {
    if (!overview || !Array.isArray(overview?.Content)) {
      return null;
    }

    return (
      <div className="p-4 md:p-6 rounded-2xl bg-white-100 flex flex-col h-full">
        <div className="flex flex-row gap-2 items-start">
          <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white">
            <CircleIcon character="📘" size={22} />
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            {overview?.RootHeading}
          </h2>
        </div>

        <p className="text-gray-700 mt-4 pl-10">{overview?.FlavorText}</p>

        <div className="space-y-2 mt-4 pl-10">
          {overview?.Content?.map((item, index) => (
            <div key={index} className="flex items-start gap-2 pl-1">
              <div className="shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[2px]">
                <CircleIcon character="" size={10} />
              </div>
              <p className="text-gray-700">{item}</p>
            </div>
          ))}
        </div>

        {overview?.ContentFollowUp && (
          <p className="text-gray-700 italic mt-4 pl-10">
            {overview.ContentFollowUp}
          </p>
        )}
      </div>
    );
  };

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
          trait: item.trait,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return { result, totalScore };
  }

  function convertToSnakeCaseObject(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return {};

    const toSnakeCase = (str) =>
      str && typeof str === "string"
        ? str
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/_+$/, "")
        : "";

    return arr.reduce((acc, item) => {
      if (!item || typeof item !== "object" || !item.Heading) return acc;

      const key = toSnakeCase(item.EnglishCode);

      if (key) acc[key] = item;

      return acc;
    }, {});
  }
  function categorizeUserTraits(observationsObject, updatedUserTraits) {
    if (!observationsObject || !updatedUserTraits?.result) return {};

    const traitMap = {
      early_childhood_development: "Early Childhood Development: {score}",
      reading_skills: "Reading Skills: {score}",
      writing_and_spelling: "Writing and Spelling: {score}",
      memory_and_sequencing: "Memory and Sequencing: {score}",
    };

    const getLevel = (score, content) => {
      const level = getDyslexiaChildrenLevelFromScore(score); // ✅ reusable logic

      const matchedItem = (content || []).find((item) =>
        item?.EnglishCode?.startsWith(level),
      );

      return {
        level,
        label: matchedItem?.Heading?.split(" ")[0] || level,
        details: matchedItem?.Content || "",
      };
    };

    let categorizedResults = {};

    Object.keys(updatedUserTraits.result).forEach((key) => {
      const traitName = traitMap[key];
      if (!traitName) return;

      const traitSection = observationsObject[key];
      if (!traitSection) return;

      const { score, trait } = updatedUserTraits?.result[key];

      const { details, label } = getLevel(score, traitSection?.Content || []);
      const localeKey =
        traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];

      categorizedResults[key] = {
        Heading: snakeToTitleCase(trait[localeKey]),
        score,
        level: label,
        details,
      };
    });

    return categorizedResults;
  }

  const updatedUserTraits = transformUserTraitsData(userTraits);

  const observationsObject = convertToSnakeCaseObject(
    observationsContent?.Content,
  );

  const categorizedData = categorizeUserTraits(
    observationsObject,
    updatedUserTraits,
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}
                      <div>
                        <OverviewCard overview={overviewcontnt} />
                      </div>

                      {/* Right Section: Image */}
                      <div className="flex items-center h-[70%] justify-center">
                        <img
                          src={result1Image}
                          alt="Banner Image 1"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.observations?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <p
                    className={`text-lg text-gray-700 w-full p-4 md:px-8 md:py-6`}
                  >
                    {contentData?.observations?.FlavorText}
                  </p>

                  <div className="w-full p-4 md:px-8 md:py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 items-stretch">
                      {[
                        {
                          key: "early_childhood_development",
                          bgClr: "bg-yellow-50",
                          textClr: "text-yellow-800",
                        },
                        {
                          key: "reading_skills",
                          bgClr: "bg-sky-50",
                          textClr: "text-sky-800",
                        },
                        {
                          key: "writing_and_spelling",
                          bgClr: "bg-violet-50",
                          textClr: "text-violet-800",
                        },
                        {
                          key: "memory_and_sequencing",
                          bgClr: "bg-rose-50",
                          textClr: "text-rose-800",
                        },
                      ].map(({ key, bgClr, textClr }) => (
                        <div key={key} className="flex flex-col h-full">
                          <h2 className="text-lg font-semibold text-gray-700 mt-6 mb-3">
                            {categorizedData?.[key]?.Heading?.split(":")[0]}
                          </h2>

                          <DescriptiveCard
                            heading={categorizedData?.[key]?.level}
                            overview={categorizedData?.[key]?.details}
                            bgClr={bgClr}
                            textClr={textClr}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t("report.dyslexia_children.what_do_this")}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 items-stretch mb-6">
                      <MeaningCard meaning={meaningContent} />

                      <div className="gap-6 sm:gap-8 flex flex-col">
                        <TipsCard tips={tipsContent} />
                        <NextStepsCard nextSteps={nextStepsContent} />
                      </div>
                    </div>
                    <p className={`text-lg text-gray-700 mt-8 mb-4`}>
                      {contentData?.conclusion}
                    </p>
                    <DescriptiveCard
                      heading={disclaimer?.RootHeading}
                      overview={disclaimer?.Content}
                      bgClr={"bg-emerald-50"}
                      textClr={"text-emerald-800"}
                    />
                  </div>
                </ExpandableSection>
              </section>
              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                <CallToActionSectionForUnAuth
                  onClick1={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`,
                    );
                  }}
                  onClick2={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`,
                    );
                  }}
                />
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getOrderDataByUserIdConnect={getOrderDataByUserIdConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
          <ReportWrapper
            assessmentId={assessmentId}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DyslexiaScreeningChildrenReport;
